<template>
  <section ref="pageSection" class="favorites-page mx-auto max-w-[720px] mt-32 lg:mt-[88px] px-16 pb-24">
    <Notification
      :message="`Removed ${removedSitterName} from favorites`"
      :trigger-notification="showRemovedNotification"
      @toggle-visibility="hideRemovedNotification"
    />
    <header class="mb-16">
      <h1 ref="pageHeading" class="text-h2" tabindex="-1">Favorites</h1>
    </header>
    <GenericError v-if="hasError" />
    <template v-else>
      <FilterToken
        v-if="showFilter"
        :button-label="filterLabel"
        :selected="careType !== null"
        modal-label="Care type"
        mobile-filters-on-larger-screens
        full-screen-on-mobile
        class="!mt-0"
      >
        <template #default="{ closeModal }">
          <RadioListFilterPanel
            :options="careTypeOptions"
            :selected-value="careType ?? 'ALL'"
            radio-group-name="Filter by care type"
            :close-modal="closeModal"
            @filter-applied="applyCareTypeFilter"
          />
        </template>
      </FilterToken>
      <p v-if="favoriteActionError && !showRemoveConfirmation" class="mb-16 text-body text-red-700" role="alert">
        Could not update favorites. Please try again.
      </p>

      <p v-if="refreshError" class="mb-16 text-body text-red-700" role="alert">
        Could not refresh favorites. Reload the page to try again.
      </p>
      <p class="sr-only" role="status">{{ isLoading ? 'Loading favorites' : '' }}</p>
      <div role="region" :aria-busy="isLoading" aria-label="Favorites results">
        <div v-if="isLoading" class="space-y-16" aria-hidden="true">
          <SitterCard v-for="n in 3" :key="n" :is-loading="true" />
        </div>

        <FavoritesEmptyState v-else-if="totalCount === 0" :filtered="careType !== null" />

        <template v-else>
          <div class="space-y-16">
            <FavoriteSitterCard
              v-for="favorite in favorites"
              :key="favorite.cardProps.sitterIdForProfile"
              :favorite="favorite"
              :removing="removingSitterId !== null"
              @remove="requestRemoval"
            />
          </div>
          <PaginationControls
            v-if="showPagination"
            class="mt-24"
            :total-items="totalCount"
            :items-per-page="itemsPerPage"
            :starting-page="currentPage"
            @page-change="handlePageChange"
          />
        </template>
      </div>
    </template>
    <ActionsPopup v-model="showRemoveConfirmation" title="Remove from favorites?" :buttons="removalButtons">
      <p class="text-body">Remove {{ pendingRemoval?.name }} from your favorites?</p>
      <p v-if="favoriteActionError" class="mt-16 text-body text-red-700" role="alert">
        Could not update favorites. Please try again.
      </p>
    </ActionsPopup>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import FilterToken from '@/components/filter_token/FilterToken.vue';
import RadioListFilterPanel from '@/components/radioButtonGroup/RadioListFilterPanel.vue';
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import Notification from '@/components/notification/Notification.vue';
import PaginationControls from '@/components/pagination_controls/PaginationControls.vue';
import SitterCard from '@/components/sitter_card/SitterCard.vue';
import FavoriteSitterCard from './FavoriteSitterCard.vue';
import FavoritesEmptyState from './FavoritesEmptyState.vue';
import { useFavorites } from './useFavorites';
import { useFavoritesFocus } from './useFavoritesFocus';
import type { ActionButton } from '@/components/actionsPopup/types';

defineOptions({ name: 'SeekerFavorites' });

const {
  isLoading,
  hasError,
  refreshError,
  favorites,
  careType,
  totalCount,
  careTypeOptions,
  filterLabel,
  applyCareTypeFilter,
  currentPage,
  itemsPerPage,
  removingSitterId,
  favoriteActionError,
  showRemovedNotification,
  removedSitterName,
  showFilter,
  showPagination,
  handlePageChange: navigateToPage,
  removeFavorite,
  hideRemovedNotification,
} = useFavorites();

const pendingRemoval = ref<{ sitterId: string; name: string } | null>(null);
const showRemoveConfirmation = computed({
  get: () => pendingRemoval.value !== null,
  set: (show: boolean) => {
    if (!show && pendingRemoval.value) {
      pendingRemoval.value = null;
      restoreRemovalTrigger();
    }
  },
});
const { pageHeading, pageSection, rememberRemovalTrigger, restoreRemovalTrigger, removalIndex, focusAfterRemoval } =
  useFavoritesFocus(isLoading, careType, currentPage, showRemoveConfirmation);

const removalButtons = computed<ActionButton[]>(() => [
  {
    id: 'confirm-remove-favorite',
    value: 'Remove',
    variant: 'primary',
    loading: removingSitterId.value !== null,
    disabled: removingSitterId.value !== null,
    onClick: confirmRemoval,
  },
  {
    id: 'cancel-remove-favorite',
    value: 'Cancel',
    variant: 'outlined',
    disabled: removingSitterId.value !== null,
    onClick: () => {
      showRemoveConfirmation.value = false;
    },
  },
]);

function handlePageChange(page: number): void {
  if (page === currentPage.value) return;

  window.scrollTo(0, 0);
  navigateToPage(page);
}

function requestRemoval(sitterId: string, name: string, event: MouseEvent): void {
  rememberRemovalTrigger(event);
  favoriteActionError.value = false;
  pendingRemoval.value = { sitterId, name };
}

async function confirmRemoval(): Promise<void> {
  if (!pendingRemoval.value) return;
  const index = removalIndex();
  if (await removeFavorite(pendingRemoval.value.sitterId)) {
    pendingRemoval.value = null;
    await focusAfterRemoval(index);
  }
}
</script>
